import { HotelDropdown } from '@/components/dropdowns';
import { HotelAccessCodeQrCell } from '@/components/hotel-access-codes/HotelAccessCodeQrCell';
import { DateTimePicker, type CrudConfig } from '@startupquickstart/frontend';
import { Link } from 'react-router-dom';
import { z } from 'zod';

/**
 * Zod schemas mirroring the server-side validation in hotel-access-codes.schemas.ts.
 */
export const createHotelAccessCodeSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().optional(),
  code: z.string().min(1),
  hotelId: z.string().uuid(),
  durationInDays: z.number().int().min(1).optional(),
  isActive: z.boolean().optional(),
  expiresAt: z.string().datetime().optional(),
});

const updateHotelAccessCodeSchema = createHotelAccessCodeSchema.partial().extend({
  revokedAt: z.string().datetime().optional(),
});

// Define type based on our Prisma schema
export interface HotelAccessCode {
  id: string;
  name: string;
  description?: string;
  code: string;
  hotelId: string;
  // Virtual field for QR code display (generated, not stored)
  qrCode?: never;
  durationInDays?: number;
  isActive: boolean;
  expiresAt?: string;
  revokedAt?: string;
  createdById?: string;
  updatedById?: string;
  createdAt: string;
  updatedAt: string;
  // Relations for display
  hotel?: {
    id: string;
    name: string;
    city: string;
    state: string;
  };
  createdBy?: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
  updatedBy?: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
}

/**
 * Hotel Access Code CRUD configuration
 */
export const hotelAccessCodeCrudConfig: CrudConfig<HotelAccessCode> = {
  entityName: 'Access Code',
  entityNamePlural: 'Access Codes',

  endpoints: {
    list: '/hotel-access-codes',
    single: (id: string) => `/hotel-access-codes/${id}`,
    create: '/hotel-access-codes',
    update: (id: string) => `/hotel-access-codes/${id}`,
    delete: (id: string) => `/hotel-access-codes/${id}`,
  },

  fields: [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'Enter access code name',
      validation: {
        min: 1,
        max: 200,
      },
    },
    {
      key: 'description',
      label: 'Description',
      type: 'richtext',
      required: false,
      placeholder: 'Enter description',
      validation: {
        max: 2000,
      },
    },
    {
      key: 'qrCode',
      label: 'QR Code',
      type: 'custom',
      required: false,
      // Left-click navigates like any other cell. It was opted out so the
      // QR could be scanned or downloaded from the list, but opting out
      // of navigation buys nothing: right-click still offers "Save image
      // as…", and scanning happens with a phone camera, not a mouse. All
      // it achieved was a dead patch across the widest column in the row,
      // which read as the row simply not being clickable.
      format: (_value: unknown, fullRecord?: HotelAccessCode) => {
        return <HotelAccessCodeQrCell accessCode={fullRecord} />;
      },
    },
    {
      key: 'code',
      label: 'Code',
      type: 'text',
      required: true,
      placeholder: 'Enter access code',
    },
    {
      key: 'hotel',
      label: 'Hotel',
      type: 'text',
      required: false,
      readonly: true,
      format: (value: { id: string; name: string } | undefined) => {
        return (
          <Link className="text-primary" to={`/hotels/${value?.id}`}>
            {value?.name || 'N/A'}
          </Link>
        );
      },
    },
    {
      key: 'hotelId',
      label: 'Hotel',
      type: 'custom',
      required: true,
      placeholder: 'Select Hotel',
      inputFormat: (
        value: string,
        onChange: (value: string) => void,
        field: { placeholder?: string },
      ) => (
        <HotelDropdown value={value} onChange={onChange} placeholder={field.placeholder} />
      ),
    },
    {
      key: 'durationInDays',
      label: 'Duration',
      type: 'number',
      required: false,
      placeholder: 'Duration in days',
      validation: {
        min: 1,
      },
      format: (value: number | undefined) =>
        value != null ? `${value} day${value === 1 ? '' : 's'}` : '—',
    },
    {
      key: 'isActive',
      label: 'Is Active',
      type: 'boolean',
      required: false,
      defaultValue: true,
    },
    {
      key: 'expiresAt',
      label: 'Expires At',
      type: 'datetime',
      required: false,
      // Render the date + time pickers inline under the single "Expires At"
      // label — the DateTimePicker's own "Date"/"Time" sub-labels would
      // otherwise create an awkward double header.
      inputFormat: (value: unknown, onChange: (next: string | undefined) => void) => (
        <DateTimePicker
          date={typeof value === 'string' ? value : undefined}
          onDateTimeChange={date => onChange(date?.toISOString())}
          dateLabel=""
          timeLabel=""
        />
      ),
    },
    {
      key: 'revokedAt',
      label: 'Revoked At',
      type: 'datetime',
      required: false,
      readonly: true,
    },
    {
      key: 'createdAt',
      label: 'Created At',
      type: 'datetime',
      readonly: true,
    },
    {
      key: 'updatedAt',
      label: 'Updated At',
      type: 'datetime',
      readonly: true,
    },
  ],

  listView: {
    // Hotel and code are intentionally omitted: in the hotel portal the list is
    // already scoped to one hotel, and the raw code adds noise (it's encoded in
    // the QR). Filters / saved list views are omitted too to keep the list lean
    // (the Filter button and the saved-views selector are both gated on
    // listView.filters in CrudListView).
    fields: ['qrCode', 'name', 'isActive', 'durationInDays', 'expiresAt', 'createdAt'],
    searchableFields: ['name', 'code'],
    sortableFields: ['name', 'code', 'createdAt', 'updatedAt'],
    defaultSort: {
      field: 'createdAt',
      direction: 'desc',
    },
    itemsPerPage: 10,
  },

  detailView: {
    sections: [
      {
        title: 'Basic Information',
        description: 'Core access code details',
        fields: ['name', 'description', 'hotel', 'isActive'],
        defaultExpanded: true,
      },
      {
        title: 'Validity',
        description: 'Access code validity information',
        fields: ['durationInDays', 'expiresAt', 'revokedAt'],
        defaultExpanded: true,
      },
      {
        title: 'System Information',
        description: 'Timestamps and system data',
        fields: ['createdAt', 'updatedAt'],
        defaultExpanded: false,
      },
    ],
  },

  form: {
    createSchema: createHotelAccessCodeSchema,
    updateSchema: updateHotelAccessCodeSchema,
    sections: [
      {
        title: 'General',
        description: 'General Information about the Access Code',
        fields: ['name', 'description', 'hotelId', 'code', 'isActive'],
        columns: 1,
      },
      {
        title: 'Validity',
        description: 'Validity Information',
        fields: ['durationInDays', 'expiresAt'],
        columns: 2,
      },
    ],
  },
};
